import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { AnimatePresence, motion } from 'framer-motion';
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import * as authActions from '../../Redux/Auth/AuthActions';
import * as systemDataActions from '../../Redux/SystemData/systemDataActions';
import ModalNavbar from './modal-navbar-component.js';
import MegaNavbarPanel from './mega-navbar-component.js';
import SystemDataService from '../../Services/SystemDataService.js';
import { getNavbarData } from '../../Public/navbar-data.js';
import SearchWithModal from '../Search/search-component.js';
import UserWidget from './navbar-user-component.js';
import { getServicesData, clearServicesCache } from '../../Public/services-data.js';
import { getProjectsData, clearProjectsCache } from '../../Public/projects-data.js';
import { getBlogsData } from '../../Public/blog-data.js';
import { getEventsData } from '../../Public/events-data.js';
import MediaComponent from '../Media/media-component.js';
// import LanguageToggle from './language-toggle-component.js'; // disabled per request
import useScreenSize from '../../Hooks/useScreenSize.js';
import { ButtonSecondary } from '../Buttons/button-component.js';

// Which nav values open a mega panel and which data section they map to.
// '/our-work' is intentionally a plain link (no mega/breadcrumb preview).
const MEGA_SECTIONS = {
    '/events': '/events',
    '/services': '/services',
    '/blog': '/blog',
};


function WhatsAppIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-6 h-6 fill-current shrink-0">
            <path d="M16 2.9C8.8 2.9 3 8.7 3 15.9c0 2.3.6 4.5 1.8 6.5L3 29.1l6.9-1.8c1.9 1 4 1.6 6.2 1.6 7.2 0 13-5.8 13-13S23.2 2.9 16 2.9zm0 23.7c-2 0-3.9-.5-5.6-1.5l-.4-.2-4.1 1.1 1.1-4-.3-.4C5.6 20 5 18 5 15.9 5 9.8 9.9 4.9 16 4.9s11 4.9 11 11-4.9 11-11 11zm6-8.2c-.3-.2-1.9-.9-2.2-1s-.5-.2-.7.2-.8 1-1 1.2-.4.2-.7.1c-.3-.2-1.4-.5-2.6-1.6-1-.9-1.6-2-1.8-2.3s0-.5.2-.7l.5-.5c.1-.2.2-.4.3-.6 0-.2 0-.4-.1-.6-.1-.2-.7-1.8-.9-2.4-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4s-1 1-1 2.4 1 2.8 1.1 3c.1.2 2 3.1 4.8 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.6-.7 1.8-1.3.2-.6.2-1.2.1-1.3-.1-.1-.3-.2-.6-.4z" />
        </svg>
    );
}

function Navbar({ overlay = false }) {
    const navigate = useHandleRedirect();
    const location = useLocation();
    const isBlogPage = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
    const dispatch = useDispatch();
    const handleRedirect = useHandleRedirect({ onReduxRedirect: (path) => dispatch(authActions.redirectScreen(path)) });

    // --- Mega menu state ---
    const [activeMega, setActiveMega] = useState(null); // stores the nav value key e.g. '/services'
    const closeTimer = useRef(null);
    const hoverRef = useRef(false);

    const openMega = (key) => {
        hoverRef.current = true;
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setActiveMega(key);
    };

    const scheduleCloseMega = () => {
        hoverRef.current = false;
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => {
            if (!hoverRef.current) setActiveMega(null);
        }, 200);
    };

    // --- transparent→solid toggle (navbar stays pinned at all times) ---
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 8);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Transparent (over a dark hero) only on overlay pages before any scroll.
    const transparent = overlay && !scrolled;

    // --- Redux ---
    const { isAuthenticated, user } = useSelector((s) => s.auth);
    const { language, dir, links = [], categories = [], services = [], banners = [], businessInfo = {} } = useSelector((s) => s.systemData || {});
    // Brand label for logo alt / aria, from per-client businessInfo, never hardcoded.
    const brandName = businessInfo?.businessName || '';
    // Active banner messages (content.bannerMessages) → announcement strip that
    // cycles through every enabled banner from the backend.
    const activeBanners = (banners || []).filter((b) => b && b.status !== false && (b.text || b.message));
    const [bannerIndex, setBannerIndex] = useState(0);

    // Cycle through active banners (fade handled via key change). Reset when the
    // set changes so the index never points past the end.
    useEffect(() => {
        setBannerIndex(0);
        if (activeBanners.length <= 1) return;
        const t = setInterval(() => {
            setBannerIndex((i) => (i + 1) % activeBanners.length);
        }, 5000);
        return () => clearInterval(t);
    }, [activeBanners.length]);

    const activeBanner = activeBanners[bannerIndex] || activeBanners[0];
    const products = useSelector((s) => Array.isArray(s.cart?.products) ? s.cart.products : []);

    // The header is position:fixed, so it doesn't occupy layout space. Rather than
    // guess its height (it changes with the announcement banner and breakpoints) and
    // reserve a hardcoded padding elsewhere, we measure it and render a same-height
    // in-flow spacer right below, content then starts flush under the navbar with no
    // gap. overlay pages skip the spacer: their hero draws its own top padding.
    const headerRef = useRef(null);
    const [headerH, setHeaderH] = useState(0);
    useEffect(() => {
        const el = headerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const measure = () => setHeaderH(el.offsetHeight);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [activeBanner, overlay]);

    // Logo variants. byLabel returns {} (truthy) when a label is missing, so fall
    // back per-slot to whichever variant actually carries an image. Two axes:
    //   colour vs white  →  solid navbar vs transparent-over-dark-hero
    //   full vs compact  →  desktop vs mobile
    //     desktop: "Logo Full" / "Logo Full White"
    //     mobile:  "Logo"      / "Logo White"
    const { isSmallScreen } = useScreenSize();
    const pickLogo = (labels) => labels
        .map((label) => SystemDataService.byLabel(links, label))
        .find((l) => l?.image) || {};

    // Primary logo is the per-client businessInfo logo (content.businessInfo.mainLogo).
    // It has no white/compact variants, so when present it's used for every slot; the
    // links-based logo variants are only a fallback for when businessInfo carries none.
    const bizLogo = businessInfo?.mainLogo || businessInfo?.logo;
    const solidLogoLink = isSmallScreen
        ? pickLogo(['Logo', 'Logo Full'])
        : pickLogo(['Logo Full', 'Logo']);
    const whiteLogoLink = isSmallScreen
        ? pickLogo(['Logo White', 'Logo Full White'])
        : pickLogo(['Logo Full White', 'Logo White']);

    const solidLogo = bizLogo ? { image: bizLogo } : solidLogoLink;
    const whiteLogo = bizLogo ? { image: bizLogo } : whiteLogoLink;
    // Transparent navbar (over the dark hero) → white logo, falling back to colour.
    const activeLogo = transparent ? (whiteLogo.image ? whiteLogo : solidLogo) : solidLogo;
    const whatsapp = SystemDataService.byLabel(links, 'WhatsApp') || SystemDataService.byLabel(links, 'Whatsapp');
    const txt = getNavbarData(language);
    const NAV_ITEMS = txt.navItems || [];

    // --- Mega panel data (refetches on language change) ---
    const [servicesDataState, setServicesDataState] = useState(services);
    const [projectsDataState, setProjectsDataState] = useState([]);
    const [blogsDataState, setBlogsDataState] = useState([]);
    const [eventsDataState, setEventsDataState] = useState([]);

    useEffect(() => {
        // Clear module-level caches so the language-specific data is fetched fresh
        clearServicesCache();
        clearProjectsCache();
        getServicesData().then(d => setServicesDataState(d || [])).catch(() => { });
        getProjectsData().then(d => setProjectsDataState(d || [])).catch(() => { });
        getBlogsData().then(d => setBlogsDataState(d || [])).catch(() => { });
        // Pass the full list; the mega panel splits it into Upcoming + Past groups.
        getEventsData(language).then(d => setEventsDataState(d || [])).catch(() => { });
    }, [language]);

    // Keep in sync if Redux services update before the direct fetch
    useEffect(() => {
        if (services.length && !servicesDataState.length) setServicesDataState(services);
    }, [services]);

    // Preload mega-panel images into browser cache so panel opens are instant
    useEffect(() => {
        const urls = [
            ...servicesDataState.flatMap(s => [s.card?.image, s.cardImage, s.image, s.hero?.imageDesktop]),
            ...projectsDataState.flatMap(p => [p.mainImage, p.image]),
            ...blogsDataState.flatMap(b => [b.heroImage, b.image, b.media]),
            ...categories.flatMap(c => [c.image, c.media]),
        ].filter(Boolean);
        const seen = new Set();
        urls.forEach(url => {
            if (seen.has(url)) return;
            seen.add(url);
            new Image().src = url;
        });
    }, [servicesDataState, projectsDataState, blogsDataState, categories]);

    // --- Active route helper ---
    const isActive = (value) => {
        if (value === '/') return location.pathname === '/';
        if (value.startsWith('#')) return false;
        return location.pathname.startsWith(value);
    };

    // Smooth-scroll to an in-page section (nav items whose value is a #anchor).
    // If we're not on the landing page, go home first, then scroll.
    const scrollToHash = (value) => {
        const id = value.replace(/^\/?#/, '');
        const el = typeof document !== 'undefined' && document.getElementById(id);
        if (el) {
            const y = el.getBoundingClientRect().top + window.scrollY - 96;
            window.scrollTo({ top: y, behavior: 'smooth' });
            return true;
        }
        return false;
    };

    const onNavClick = (e, value) => {
        setActiveMega(null);
        if (value.startsWith('#') || value.startsWith('/#')) {
            e.preventDefault();
            if (!scrollToHash(value)) {
                navigate('/');
                setTimeout(() => scrollToHash(value), 450);
            }
            return;
        }
        handleRedirect(e, value);
    };

    return (
      <>
        <header
            ref={headerRef}
            dir={dir || 'rtl'}
            className="fixed top-0 left-0 w-full z-50"
        >
            {/* Announcement strip, cycles active banner messages (content.bannerMessages) */}
            {activeBanner && (
                <div className="w-full bg-secondary-500 overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.button
                            key={activeBanner.id ?? bannerIndex}
                            initial={{ y: '100%', opacity: 0 }}
                            animate={{ y: '0%', opacity: 1 }}
                            exit={{ y: '-100%', opacity: 0 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => onNavClick(e, activeBanner.value || '#contact')}
                            className="w-full px-4 py-1.5 text-center text-sm font-semibold text-white hover:bg-secondary-600 transition-colors"
                        >
                            {activeBanner.text || activeBanner.message}
                        </motion.button>
                    </AnimatePresence>
                </div>
            )}

            <nav className={`w-full border-b transition-colors duration-300 ${transparent ? 'bg-transparent border-transparent' : 'bg-white/95 backdrop-blur-md border-primary-100 shadow-sm'}`}>
                <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 h-[86px] flex items-center justify-between gap-4">

                    {/* ── LEFT: Logo (desktop) / Hamburger (mobile) ── */}
                    <div className="flex-shrink-0 flex items-center gap-3">
                        <button
                            onClick={(e) => handleRedirect(e, '/')}
                            className="hidden md:flex items-center justify-center h-14"
                            aria-label={brandName ? `${brandName}, home` : 'Home'}
                        >
                            {activeLogo?.image && (
                                <MediaComponent src={activeLogo.image} quality={50} useSekeleton={false} alt={brandName ? `${brandName} logo` : 'Logo'} wrapperClassName="h-14 w-auto max-w-[240px]" objectFit="contain" />
                            )}
                        </button>
                        <div className="md:hidden flex items-center gap-2">
                            <ModalNavbar formatter={NAV_ITEMS} onDark={transparent} />
                            {isBlogPage && <SearchWithModal iconOnly />}
                        </div>
                    </div>

                    {/* ── CENTER: Desktop nav links ── */}
                    <div className={`hidden md:flex items-center justify-center gap-0 text-[15px] font-semibold font-primary flex-1 transition-colors duration-300 text-primary-500`}>
                        {NAV_ITEMS.map((item, index) => {
                            const hasMega = !!MEGA_SECTIONS[item.value];
                            const active = isActive(item.value) || activeMega === item.value;
                            return (
                                <div
                                    key={item.id}
                                    className="flex items-center"
                                    onMouseEnter={hasMega ? () => openMega(item.value) : scheduleCloseMega}
                                    onMouseLeave={hasMega ? scheduleCloseMega : undefined}
                                >
                                    <button
                                        onClick={(e) => onNavClick(e, item.value)}
                                        className={`inline-flex items-center gap-1 px-3.5 py-2 transition-colors whitespace-nowrap ${active ? 'text-secondary-500' : 'hover:text-primary-900'}`}
                                    >
                                        {item.name}
                                        {hasMega && (
                                            <svg
                                                viewBox="0 0 24 24" fill="none"
                                                className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${activeMega === item.value ? 'rotate-180' : ''}`}
                                            >
                                                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </button>
                                    {index < NAV_ITEMS.length - 1 && (
                                        <span className={`h-4 w-px shrink-0 ${transparent ? 'bg-white/20' : 'bg-primary-100'}`} />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* ── RIGHT: Search + Language + User + Free Review CTA ── */}
                    <div className="flex-shrink-0 flex items-center gap-3 md:gap-4">
                        {isBlogPage && (
                            <div className="hidden md:flex">
                                <SearchWithModal iconOnly />
                            </div>
                        )}
                        {/* Language toggle disabled per request, single-language display. */}
                        {/* <LanguageToggle /> */}
                        {isAuthenticated &&
                            <UserWidget
                                user={user}
                                isAuthenticated={isAuthenticated}
                                links={links}
                                onDark={transparent}
                                onNavigate={(path) => navigate(path)}
                                onLogout={() => dispatch(authActions.logout())}
                            />
                        }
                        {whatsapp?.url ? (
                            <a
                                href={whatsapp.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hidden md:flex items-center gap-2 bg-secondary-500 hover:bg-secondary-600 text-white rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200"
                            >
                                {whatsapp.description}
                                {whatsapp.image
                                    ? <MediaComponent src={whatsapp.image} useSekeleton={false} alt="WhatsApp" wrapperClassName="w-5 h-5 shrink-0" objectFit="contain" />
                                    : <WhatsAppIcon />
                                }
                            </a>
                        ) : (
            /* ClearViolations primary CTA, scrolls to the free-review form.
               Wrapped so the button is hidden on mobile (button overrides its own className). */
                            <div className="hidden md:flex">
                                <ButtonSecondary
                                    onClick={(e) => onNavClick(e, '#contact')}
                                    dir="ltr"
                                    size="md"
                                    className="items-center !rounded-full !px-6 text-sm font-bold uppercase tracking-wide shadow-lg shadow-secondary-500/25"
                                >
                                    Free Review →
                                </ButtonSecondary>
                            </div>
                        )}
                        {/* Mobile: logo centered in the bar (hamburger left, controls right). */}
                        <button onClick={(e) => handleRedirect(e, '/')} className="md:hidden absolute left-1/2 -translate-x-1/2 flex items-center justify-center h-12" aria-label={brandName ? `${brandName}, home` : 'Home'}>
                            {activeLogo?.image && (
                                <MediaComponent src={activeLogo.image} quality={30} useSekeleton={false} alt={brandName ? `${brandName} logo` : 'Logo'} wrapperClassName="h-10 w-auto max-w-[150px]" objectFit="contain" />
                            )}
                        </button>
                    </div>

                </div>
            </nav>

            {/* ── MEGA PANEL (desktop) ── */}
            <AnimatePresence>
                {activeMega && (
                    <motion.div
                        key="mega"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="hidden md:flex absolute top-full left-0 w-full justify-center px-6 pb-4 pt-1"
                    >
                        <MegaNavbarPanel
                            section={MEGA_SECTIONS[activeMega]}
                            servicesData={servicesDataState}
                            categoriesData={categories}
                            projectsData={projectsDataState}
                            blogsData={blogsDataState}
                            eventsData={eventsDataState}
                            productsData={products}
                            onNavigate={(href) => { navigate(href); setActiveMega(null); }}
                            onMouseLeavePanel={scheduleCloseMega}
                            onMouseEnterPanel={() => { hoverRef.current = true; if (closeTimer.current) clearTimeout(closeTimer.current); }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
        {/* In-flow spacer reserving the fixed header's measured height. Skipped on
            overlay pages, where the hero sits under the transparent navbar. */}
        {!overlay && <div aria-hidden style={{ height: headerH }} />}
      </>
    );
}

export default Navbar;
